import * as _ from 'lodash';
import { Utils } from "../Utils/Utils";
import { StringUtils } from "../Utils/StringUtils";
import Promise = require('bluebird');

/**
 * instance of dialog, used mostly in dialog events
 */
export interface IDialogInstance {
    /**
     * opens dialog
     */
    open(): void;

    /**
     * closes dialog
     */
    close(): void;

    /**
     * gets raw modal JQuery element
     */
    getModal(): JQuery;

    /**
     * gets raw modal dialog JQuery element
     */
    getModalDialog(): JQuery;

    /**
     * gets raw modal content JQuery element
     */
    getModalContent(): JQuery;

    /**
     * gets raw modal header JQuery element
     */
    getModalHeader(): JQuery;

    /**
     * gets raw modal body JQuery element
     */
    getModalBody(): JQuery;

    /**
     * gets raw modal footer JQuery element
     */
    getModalFooter(): JQuery;

    /**
     * gets data value specified by key associated with dialog.
     */
    getData<T>(key: string): T;

    /**
     * sets dialog data under specified key with its value.
     */
    setData<T>(key: string, value: T): void;

    enableButtons(enable: boolean): void;
    setClosable(closable: boolean): void;
    realize(): void;

    /**
     * gets button specified by id.
     */
    getButton(id: string): DialogButton;
}

/**
 * Dialog utils (alert, error, info, ...)
 */
export class Dialog {
    private static toCssClass(dialogType?: DialogType, prefix: string = ''): string {
        if (Utils.isNullOrUndefined(dialogType)) {
            return '';
        }

        let res = dialogType.toString().split('-')[1];
        if (!StringUtils.isNullOrEmpty(prefix)) {
            res = prefix + res;
        }

        return res;
    }

    /**
     * shows dialog with specified options.
     */
    public static show(options: DialogOptions): void {
        options.animate = false;
        BootstrapDialog.show(options);
    }

    /**
     * shows information dialog.
     */
    public static info(message: DialogMessageType, title: DialogTitleType): Promise<any> {
        return Dialog.alert(message, title, DialogType.Info);
    }

    /**
     * shows error dialog.
     */
    public static error(message: DialogMessageType, title: DialogTitleType = 'Error'): Promise<any> {
        return Dialog.alert(message, title, DialogType.Danger);
    }

    /**
     * shows alert dialog.
     */
    public static alert(message: DialogMessageType, title: DialogTitleType = null, type: DialogType = DialogType.Default,
        size: DialogSize = DialogSize.Normal): Promise<any> {

        return new Promise<any>((resolve: any, reject: any) => {
            let button: IDialogButton = {
                label: 'Ok',
                cssClass: Dialog.toCssClass(type, 'btn-'),
                action: (dialogRef: IDialogInstance) => {
                    dialogRef.close();
                    resolve();
                }
            };

            let options: DialogOptions = {
                message: message,
                title: title,
                type: type,
                size: size,
                draggable: true,
                buttons: [button]
            };
            Dialog.show(options);
        });
    }
}

/**
 * Dialog types.
 */
export enum DialogType {
    Default = <any>'type-default',
    Info = <any>'type-info',
    Primary = <any>'type-primary',
    Success = <any>'type-success',
    Warning = <any>'type-warning',
    Danger = <any>'type-danger'
}

/**
 * Dialog sizes.
 */
export enum DialogSize {
    Normal = <any>'size-normal',
    Wide = <any>'size-wide',
    Large = <any>'size-large'
}

/**
 * Dialog options.
 */
export interface DialogOptions {
    /**
     * Dialog type.
     */
    type: DialogType;

    /**
     * Dialog size.
     */
    size: DialogSize;

    /**
     * Dialog css class.
     */
    cssClass?: string;

    /**
     * Dialog title.
     */
    title: DialogTitleType;

    /**
     * Dialog message.
     */
    message: DialogMessageType;

    /**
     * Dialog buttons.
     */
    buttons: IDialogButton[];

    /**
     * Dialog flag whenever dialog is closeable or not.
     */
    closable?: boolean;
    spinicon?: string;

    /**
     * Dialog flag whenever dialog can be draggable or not.
     */
    draggable?: boolean;

    /**
     * Dialog flag whenever dialog should be animated when showing.
     */
    animate?: boolean;
    data?: Object;
    autodestroy?: boolean;
    onshow?: DialogEvent;
    onshown?: DialogEvent;
    onhide?: DialogEvent;
    onhidden?: DialogEvent;
}

export interface IDialogButton {
    label: string;
    action: DialogEvent;
    id?: string;
    icon?: string;
    cssClass?: string;
    data?: Object;
    autospin?: boolean;
    hotkey?: number;
}

export interface DialogButton extends IDialogButton {
    toggleEnable(enabled: boolean): void;
    toggleSpin(enabled: boolean): void;
    enable(): void;
    disable(): void;
    spin(): void;
    stopSpin(): void;
}

export type DialogEvent = (dialog?: IDialogInstance) => void | boolean;
export type DialogGetStringEvent = (dialog?: IDialogInstance) => string;
export type DialogTitleType = string | JQuery | DialogGetStringEvent;
export type DialogMessageType = string | JQuery | DialogGetStringEvent;


/** WEBPACK FOOTER **
 ** ./Typescript/Widgets/Dialog.ts
 **/